import type React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { ReferenceQualitySummary } from "../../api/reports";
import { formatEuro } from "../../lib/format";

type Quality = "sufficient" | "insufficient";

export function ReferenceQualityChoice({ value, onChange, query, aside }: {
  aside?: React.ReactNode;
  value: Quality;
  onChange: (value: Quality) => void;
  query: UseQueryResult<ReferenceQualitySummary, Error>;
}) {
  // Kompakter Umschalter statt zwei Karten und Erklärabsatz (#630).
  return <section aria-label="Vergleichsbasis" className="mt-4 flex flex-wrap items-center gap-3">
    <div role="group" aria-label="Vergleichsbasis wählen" className="inline-flex rounded-md border border-slate-300 bg-white p-0.5 text-sm shadow-sm">
      {(["sufficient", "insufficient"] as const).map((quality) => {
        const summary = query.data?.[quality];
        const label = quality === "sufficient" ? "Ausreichende Vergleichsbasis" : "Zu wenig Vergleichsdaten";
        const hint = quality === "sufficient"
          ? "Mindestzahl früherer Positionen und Belege erreicht. Ob Preise und Konditionen vergleichbar sind, prüfst du am Original."
          : "Zu wenig frühere Vergleichsdaten: rechnerischer Unterschied, keine bestätigten Mehrkosten.";
        return <button key={quality} type="button" aria-pressed={value === quality} onClick={() => onChange(quality)} title={summary ? `${hint} ${formatEuro(summary.difference)} positive Preisunterschiede.` : hint}
          className={`rounded px-3 py-1 font-medium ${value === quality ? "bg-emerald-600 text-white" : "text-slate-600 hover:text-slate-900"}`}>
          {label}
          <span className={`ml-1.5 tabular-nums ${value === quality ? "text-emerald-100" : "text-slate-400"}`} aria-live="polite">
            {query.isFetching ? "…" : summary ? summary.count.toLocaleString("de-AT") : "–"}
          </span>
        </button>;
      })}
    </div>
    {query.isError ? <button type="button" onClick={() => void query.refetch()} className="text-sm text-emerald-700 underline">Zahlen erneut laden</button> : null}
    {aside ? <div className="ml-auto">{aside}</div> : null}
  </section>;
}